import { useCallback, useRef, useState } from "react";

import {
  BuiStatusBadge,
  type IBuiStatusBadgeProps,
  type TBuiStatusBadgeDisplayTypes,
} from "@btnet/blueprint-components/BuiStatusBadge";

import { BTPopover } from "../BTPopover/BTPopover";

export type TBTStatusBadgeDisplayTypes = TBuiStatusBadgeDisplayTypes;

/**
 * Props for the BTStatusBadge component.
 *
 * BTStatusBadge wraps the BuiStatusBadge primitive and adds the truncation-aware tooltip
 * behavior. Its props mirror BuiStatusBadge, minus `textRef` (BTStatusBadge owns the text ref
 * internally).
 */
export type IBTStatusBadgeProps = Omit<IBuiStatusBadgeProps, "textRef">;

/**
 * BTStatusBadge - an app-facing status badge.
 *
 * Wraps the blueprint `BuiStatusBadge` primitive and adds truncation-aware tooltip behavior: when
 * the badge text overflows its container, the full text is shown in a `BTPopover` on hover. The
 * color and default icon are driven by `displayType`; extend the pill with tailwind utility classes
 * via `className`.
 *
 * Truncation is measured lazily, only when the popover is about to open, rather than on every
 * mount. Measuring eagerly (via a mount-time `ResizeObserver`) is cheap for a badge that mounts
 * once, but inside a virtualized grid where badges mount/unmount every scroll frame it forces a
 * synchronous layout read per cell per frame, causing scroll jank (ado-281012).
 *
 * @example
 * <BTStatusBadge testid="status" displayType="success" text="Approved" />
 */
export function BTStatusBadge(props: IBTStatusBadgeProps) {
  const { text } = props;
  const textRef = useRef<HTMLDivElement>(null);

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const handleVisibleChange = useCallback((requestedVisible: boolean) => {
    if (!requestedVisible) {
      setIsPopoverVisible(false);
      return;
    }

    const textEl = textRef.current;
    // scrollWidth > clientWidth indicates overflow/truncation when using ellipsis.
    const isTruncated = !!textEl && textEl.scrollWidth > textEl.clientWidth;
    setIsPopoverVisible(isTruncated);
  }, []);

  return (
    <BTPopover
      content={text}
      disabled={!text}
      visible={isPopoverVisible}
      onVisibleChange={handleVisibleChange}
    >
      <BuiStatusBadge textRef={textRef} {...props} />
    </BTPopover>
  );
}
